import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { profileDetailsColumns } from '../../services/dataService';
import {
  ProfileFields,
  ProfileFormValue,
  emptyProfileForm,
  resolveProfileForm
} from '../components/ProfileFields';

const Register: React.FC = () => {
  const { refreshProfile } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [profileForm, setProfileForm] = useState<ProfileFormValue>(emptyProfileForm);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const navigate = useNavigate();

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Validates every FR-02 field, then turns the hospital picker's choice
      // into a control_places row before signup, so the trigger has an id to
      // attach (DF-09).
      const { details, place, doctor } = await resolveProfileForm(profileForm);

      // Registration data rides in user_metadata. The profile row is created
      // by a database trigger (migration 009), not here: with email
      // confirmation on there is no session immediately after signUp, so a
      // client-side insert fails and strands the account without a profile
      // (DF-08).
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/login`,
          // The same column shape the profile editor writes, so a profile
          // created at signup and one edited later cannot differ.
          data: profileDetailsColumns(details, place, doctor)
        }
      });

      if (authError) throw authError;

      // No session means the project requires email confirmation. Say so
      // rather than dropping the user on a login screen that will reject them.
      if (!authData.session) {
        setAwaitingConfirmation(true);
        return;
      }

      if (authData.user) {
        // Refresh profile in context so ProtectedRoute knows we are good
        await refreshProfile();

        // 3. Navigate to Home
        navigate('/');
      }
    } catch (err: any) {
      setError(err.message || 'Gagal mendaftar. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  };

    const handleGoogleRegister = async () => {
    try {
      setLoading(true);
      setError(null);
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
        },
      });
      if (error) throw error;
    } catch (err: any) {
      setError(err.message || 'Gagal mendaftar dengan Google.');
      setLoading(false);
    }
  };


  if (awaitingConfirmation) {
    return (
      <div className="screen min-h-[100dvh] h-full">
        <div className="screen-card text-center" role="status">
          <div className="section-title">Cek email kamu</div>
          <div className="subtitle">
            Kami mengirim tautan verifikasi ke <strong>{email}</strong>. Buka
            tautan itu untuk mengaktifkan akun, lalu masuk.
          </div>
          <div className="subtitle" style={{ fontSize: 11, marginTop: 8 }}>
            Belum ada emailnya? Cek folder spam, atau daftar ulang dengan
            alamat email yang benar.
          </div>
          <div className="next-actions" style={{ marginTop: 16, justifyContent: 'center' }}>
            <button type="button" className="btn-primary" onClick={() => navigate('/login')}>
              Ke halaman masuk
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="screen-card">
        <div className="section-title">Daftar Akun</div>
        <div className="subtitle">
          Lengkapi data diri Anda untuk mulai menggunakan aplikasi.
        </div>

        <form onSubmit={handleRegister}>
          <ProfileFields
            value={profileForm}
            onChange={setProfileForm}
            disabled={loading}
            idPrefix="register"
          />

          <div className="field-group" style={{ marginTop: 16 }}>
            <div className="field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="contoh@email.com"
              />
            </div>

            <div className="field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Minimal 6 karakter"
                minLength={6}
              />
            </div>
          </div>

          {error && (
            <div className="subtitle" style={{ color: '#b91c1c', marginTop: 16 }}>
              {error}
            </div>
          )}

          <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
            >
              {loading ? 'Mendaftar...' : 'Daftar'}
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ flex: 1, height: '1px', backgroundColor: '#e2e8f0' }}></div>
              <span style={{ fontSize: '14px', color: '#64748b' }}>atau</span>
              <div style={{ flex: 1, height: '1px', backgroundColor: '#e2e8f0' }}></div>
            </div>

            <button
              type="button"
              className="btn-google w-full"
              onClick={handleGoogleRegister}
              disabled={loading}
            >
              <svg className="w-5 h-5" width="20" height="20" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
              Daftar dengan Google
            </button>
          </div>

          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <span className="subtitle">Sudah punya akun? </span>
            <Link to="/login" style={{ color: 'var(--app-color-primary)', fontWeight: 'bold' }}>
              Masuk di sini
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;
